declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export interface GoogleAdsConfig {
  conversionId: string;
  purchaseLabel?: string;
  beginCheckoutLabel?: string;
  addPaymentInfoLabel?: string;
  addShippingInfoLabel?: string;
  enhancedConversionsEnabled?: boolean;
}

export interface CheckoutTrackingItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  variant?: string;
  category?: string;
}

export interface CustomerTrackingData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

let isInitialized = false;
let activeConfig: GoogleAdsConfig | null = null;

export const googleAdsTracker = {
  init(config: GoogleAdsConfig) {
    if (!config.conversionId || isInitialized) return;
    activeConfig = config;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer?.push(arguments);
    };

    const scriptId = "google-ads-gtag";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.conversionId}`;
      document.head.appendChild(script);
    }

    window.gtag("js", new Date());
    window.gtag("config", config.conversionId, {
      allow_enhanced_conversions: config.enhancedConversionsEnabled ?? true,
      linker: {
        domains: ["melhot.com.br", "checkout.melhot.com.br"],
        accept_incoming: true
      }
    });

    isInitialized = true;
  },

  trackBeginCheckout(items: CheckoutTrackingItem[], totalValue: number, currency: string = "BRL") {
    if (!window.gtag || !activeConfig?.conversionId) return;

    const eventData: Record<string, any> = {
      value: totalValue / 100,
      currency,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price / 100,
        quantity: item.quantity,
        item_variant: item.variant
      }))
    };

    if (activeConfig.beginCheckoutLabel) {
      eventData.send_to = `${activeConfig.conversionId}/${activeConfig.beginCheckoutLabel}`;
    }

    window.gtag("event", "begin_checkout", eventData);
  },

  trackAddShippingInfo(shippingMethod: string, items: CheckoutTrackingItem[], totalValue: number, currency: string = "BRL") {
    if (!window.gtag || !activeConfig?.conversionId) return;

    const eventData: Record<string, any> = {
      value: totalValue / 100,
      currency,
      shipping_tier: shippingMethod,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price / 100,
        quantity: item.quantity
      }))
    };

    if (activeConfig.addShippingInfoLabel) {
      eventData.send_to = `${activeConfig.conversionId}/${activeConfig.addShippingInfoLabel}`;
    }

    window.gtag("event", "add_shipping_info", eventData);
  },

  trackAddPaymentInfo(paymentMethod: string, items: CheckoutTrackingItem[], totalValue: number, currency: string = "BRL") {
    if (!window.gtag || !activeConfig?.conversionId) return;

    const eventData: Record<string, any> = {
      value: totalValue / 100,
      currency,
      payment_type: paymentMethod,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price / 100,
        quantity: item.quantity
      }))
    };

    if (activeConfig.addPaymentInfoLabel) {
      eventData.send_to = `${activeConfig.conversionId}/${activeConfig.addPaymentInfoLabel}`;
    }

    window.gtag("event", "add_payment_info", eventData);
  },

  trackPurchase(
    transactionId: string,
    totalValue: number,
    items: CheckoutTrackingItem[],
    customerData?: CustomerTrackingData,
    currency: string = "BRL"
  ) {
    if (!window.gtag || !activeConfig?.conversionId) return;

    if (customerData && (activeConfig.enhancedConversionsEnabled ?? true)) {
      const userData: Record<string, any> = {};
      if (customerData.email) userData.email = customerData.email.trim().toLowerCase();
      if (customerData.phone) userData.phone_number = customerData.phone.replace(/\D/g, "");
      
      const address: Record<string, any> = {};
      if (customerData.firstName) address.first_name = customerData.firstName.trim();
      if (customerData.lastName) address.last_name = customerData.lastName.trim();
      if (customerData.street) address.street = customerData.street.trim();
      if (customerData.city) address.city = customerData.city.trim();
      if (customerData.state) address.region = customerData.state.trim();
      if (customerData.zip) address.postal_code = customerData.zip.replace(/\D/g, "");
      address.country = customerData.country || "BR";

      if (Object.keys(address).length > 0) {
        userData.address = address;
      }

      window.gtag("set", "user_data", userData);
    }

    const sendTo = activeConfig.purchaseLabel
      ? `${activeConfig.conversionId}/${activeConfig.purchaseLabel}`
      : activeConfig.conversionId;

    window.gtag("event", "conversion", {
      send_to: sendTo,
      transaction_id: transactionId,
      value: totalValue / 100,
      currency,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price / 100,
        quantity: item.quantity
      }))
    });

    window.gtag("event", "purchase", {
      transaction_id: transactionId,
      value: totalValue / 100,
      currency,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price / 100,
        quantity: item.quantity
      }))
    });
  }
};
